import { IFileSizes } from "../../jsxProviders";
import { Scene } from "../types";
import { pluralize } from "./Pluralize";

/**
 * Method that formats the date in readable form
 * if date is today, return the time
 * else return the date in the form dd-mm-yy at time
 * @param date
 */
export const dateFormatter = (date: Date): string => {
  const day: string | number = date.getDate();

  const month: string | number = date.toLocaleDateString("default", {
    month: "short",
  });

  const year: string | number = date.getFullYear();

  let minutes: number | string = date.getMinutes();
  if (minutes < 10) minutes = "0" + minutes;
  const hourAndMinute = `${date.getHours()}:${minutes}`;

  const today = new Date();
  const timeElapsed = today.getTime() - date.getTime();

  // If date is in the last 60 seconds
  if (timeElapsed / 1000 < 60) return "Seconds ago";
  // If date is in the last 60 minutes
  if (timeElapsed / 1000 / 60 < 60) {
    const minutes = Math.floor(timeElapsed / 1000 / 60);
    return `${minutes} ${pluralize("minute", minutes)} ago`;
  }
  // If date is in the last 24 hours
  if (timeElapsed / 1000 / 60 / 60 < 24) {
    const hours = Math.floor(timeElapsed / 1000 / 60 / 60);
    return `${hours} ${pluralize("hour", hours)} ago`;
  }
  // If date is in the last 7 days
  if (timeElapsed / 1000 / 60 / 60 / 24 < 7) {
    const days = Math.floor(timeElapsed / 1000 / 60 / 60 / 24);
    return `${days} ${pluralize("day", days)} ago`;
  }
  // If date is in the same year
  if (today.getFullYear() === year) return `${month} ${day}, ${hourAndMinute}`;
  // If date is in a different year
  else return `${month} ${day} ${year}, ${hourAndMinute}`;
};
export const sortByDate = (aDate: string, bDate: string) => {
  if (aDate === bDate) return 0; // If they are equal
  if (typeof aDate === "string") {
    if (typeof bDate === "string") {
      return new Date(bDate).getTime() - new Date(aDate).getTime(); // If they are both strings
    } else {
      return -1; // If a is a string, b is not
    }
  } else if (typeof bDate === "string") {
    return 1; // If b is a string and a is not
  }
  return bDate === null ? 1 : -1; // Sort by the composition that is rendered, the one that is not rendered comes after
};
/**
 * Filters compositions and returns an array of compositions that have been rendered today
 * @param filteredCompositions
 * @param scenes
 * @returns
 */
export const compositionsFromToday = (
  filteredCompositions: {
    name: string;
    id: number;
  }[],
  scenes: Scene[] | undefined
) => {
  return filteredCompositions.filter((comp) => {
    const currentScene = scenes?.find((scene) => scene?.name === comp.name);
    if (currentScene?.updatedAt) {
      const date = new Date(currentScene.updatedAt);
      const today = new Date();
      return (
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear()
      );
    }
  });
};

export const compositionsRendered = (
  rendered: boolean,
  filteredCompositions: {
    name: string;
    id: number;
  }[],
  scenes: Scene[] | undefined
) => {
  return filteredCompositions.filter((comp) =>
    rendered
      ? !!scenes?.find((scene) => scene?.name === comp.name)
      : !scenes?.find((scene) => scene?.name === comp.name)
  );
};

/**
 *  Method that returns the file format
 * @param file 
 * @returns 
 */
export const fileFormat = (file: IFileSizes) => {
  const videoExtensions = ["mp4", "avi", "mov", "wmv", "flv", "mkv"];
  const imageExtensions = ["jpg", "jpeg", "png", "gif", "webp", "tiff", "bmp"];
  const audioExtensions = ["mp3", "wav", "aac", "m4a", "flac"];

  const extension = file.name.split(".").pop()?.toLowerCase() || "";
  if (videoExtensions.includes(extension)) return "video";
  if (imageExtensions.includes(extension)) return "image";
  if (audioExtensions.includes(extension)) return "audio";
  return "other";
};
